import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import FirestoreService from '../services/firestoreService';

export interface UserProfile {
  name: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  address: string;
  avatar?: string;
}

export interface UserPreferences {
  notifications: {
    email: boolean;
    sms: boolean;
    push: boolean;
    deals: boolean;
    expiry: boolean;
    transfers: boolean;
  };
  privacy: {
    dataCollection: boolean;
    personalization: boolean;
    marketing: boolean;
    thirdParty: boolean;
  };
  theme: 'light' | 'dark';
}

export const useUserData = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch user data when user is authenticated
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) {
        console.log('🔥 useUserData: No user, clearing data');
        setProfile(null);
        setPreferences(null);
        setLoading(false);
        return;
      }

      try {
        console.log('🔥 useUserData: Fetching data for user:', user.id);
        setLoading(true);
        setError(null);

        // Fetch profile and preferences in parallel
        const [profileData, preferencesData] = await Promise.all([
          FirestoreService.getUserProfile(user.id),
          FirestoreService.getUserPreferences(user.id)
        ]);

        console.log('🔥 useUserData: Profile data received:', profileData);
        console.log('🔥 useUserData: Preferences data received:', preferencesData);

        // If profile or preferences are null, try to fix the document structure
        if (!profileData || !preferencesData) {
          console.log('🔧 useUserData: Missing profile/preferences, attempting to fix document');
          await FirestoreService.fixUserDocument(user.id);
          
          // Fetch again after fixing
          const [newProfileData, newPreferencesData] = await Promise.all([
            FirestoreService.getUserProfile(user.id),
            FirestoreService.getUserPreferences(user.id)
          ]);
          
          console.log('🔧 useUserData: After fix - Profile:', newProfileData);
          console.log('🔧 useUserData: After fix - Preferences:', newPreferencesData);
          
          setProfile(newProfileData);
          setPreferences(newPreferencesData);
        } else {
          setProfile(profileData);
          setPreferences(preferencesData);
        }

        console.log('✅ User data fetched successfully');
      } catch (err: any) {
        setError(err.message || 'Failed to fetch user data');
        console.error('❌ Error fetching user data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  // Save profile data
  const saveProfile = async (profileData: UserProfile): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      setError(null);
      await FirestoreService.saveUserProfile(user.id, profileData);
      setProfile(profileData);
      console.log('✅ Profile saved successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to save profile');
      console.error('❌ Error saving profile:', err);
      throw err;
    }
  };

  // Save preferences data
  const savePreferences = async (preferencesData: UserPreferences): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      setError(null);
      await FirestoreService.saveUserPreferences(user.id, preferencesData);
      setPreferences(preferencesData);
      console.log('✅ Preferences saved successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to save preferences');
      console.error('❌ Error saving preferences:', err);
      throw err;
    }
  };

  // Update specific preference values
  const updatePreference = async (
    category: keyof UserPreferences,
    key: string,
    value: any
  ): Promise<void> => {
    if (!preferences) return;

    const updatedPreferences = {
      ...preferences,
      [category]: {
        ...(preferences[category] as any),
        [key]: value,
      },
    };

    await savePreferences(updatedPreferences);
  };

  // Update profile field
  const updateProfile = async (
    updates: Partial<UserProfile>
  ): Promise<void> => {
    if (!profile) return;

    const updatedProfile = {
      ...profile,
      ...updates,
    };

    await saveProfile(updatedProfile);
  };

  return {
    profile,
    preferences,
    loading,
    error,
    saveProfile,
    savePreferences,
    updatePreference,
    updateProfile,
    isAuthenticated: !!user,
  };
};